import React, { useState } from 'react';
import { Search as SearchIcon, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { indonesiaTours, worldwideTours, coffeeCivilization } from '../data/tours';
import { worldwideDestinations, indonesiaGeographicGroups, worldwideContinents } from '../data/mock';

export const GlobalSearch = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const navigate = useNavigate();

  const searchContent = (query) => {
    if (!query || query.trim().length < 2) {
      setResults([]);
      return;
    }

    const searchTerm = query.toLowerCase();
    const foundResults = [];

    // Search Indonesia Tours
    indonesiaTours.forEach(tour => {
      if (
        tour.title?.toLowerCase().includes(searchTerm) ||
        tour.subtitle?.toLowerCase().includes(searchTerm) ||
        tour.destination?.toLowerCase().includes(searchTerm) ||
        tour.highlights?.some(h => h.toLowerCase().includes(searchTerm))
      ) {
        foundResults.push({
          type: 'Indonesia Journey',
          title: tour.title,
          subtitle: tour.destination,
          url: `/tours/${tour.id}`
        });
      }
    });

    // Search Worldwide Tours
    worldwideTours.forEach(tour => {
      if (
        tour.title?.toLowerCase().includes(searchTerm) ||
        tour.subtitle?.toLowerCase().includes(searchTerm) ||
        tour.destination?.toLowerCase().includes(searchTerm) ||
        tour.highlights?.some(h => h.toLowerCase().includes(searchTerm))
      ) {
        foundResults.push({
          type: 'Worldwide Journey',
          title: tour.title,
          subtitle: tour.destination,
          url: `/tours/${tour.id}`
        });
      }
    });

    // Search Coffee Civilization
    if (coffeeCivilization.title?.toLowerCase().includes(searchTerm) ||
        coffeeCivilization.subtitle?.toLowerCase().includes(searchTerm) ||
        'coffee'.includes(searchTerm)) {
      foundResults.push({
        type: 'Special Experience',
        title: coffeeCivilization.title,
        subtitle: coffeeCivilization.destination,
        url: '/CoffeeCivilization'
      });
    }

    // Search Destinations
    worldwideDestinations.forEach(dest => {
      if (dest.name?.toLowerCase().includes(searchTerm) ||
          dest.description?.toLowerCase().includes(searchTerm)) {
        foundResults.push({
          type: 'Destination',
          title: dest.name,
          subtitle: dest.description.substring(0, 80) + '...',
          url: `/worldwide-journeys/${dest.continent}/${dest.region}/${dest.slug}`
        });
      }
    });

    // Search Indonesia Geographic Groups
    indonesiaGeographicGroups.forEach(group => {
      if (group.name?.toLowerCase().includes(searchTerm) ||
          group.description?.toLowerCase().includes(searchTerm)) {
        foundResults.push({
          type: 'Indonesia Region',
          title: group.name,
          subtitle: group.description.substring(0, 80) + '...',
          url: `/indonesia-journeys/${group.id}`
        });
      }
    });

    // Search Continents
    worldwideContinents.forEach(continent => {
      if (continent.name?.toLowerCase().includes(searchTerm)) {
        foundResults.push({
          type: 'Continent',
          title: continent.name,
          subtitle: continent.description.substring(0, 80) + '...',
          url: `/worldwide-journeys/${continent.id}`
        });
      }
    });

    setResults(foundResults.slice(0, 10)); // Limit to 10 results
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    searchContent(query);
  };

  const handleResultClick = (url) => {
    navigate(url);
    setIsOpen(false);
    setSearchQuery('');
    setResults([]);
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        style={{
          background: 'transparent',
          border: '1px solid #FBBF24',
          color: '#FBBF24',
          padding: '8px 16px',
          borderRadius: '4px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: '500',
          transition: 'all 0.3s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#FBBF24';
          e.currentTarget.style.color = '#070D1E';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = '#FBBF24';
        }}
      >
        <SearchIcon size={16} />
        <span>Search</span>
      </button>

      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: '10vh'
          }}
          onClick={() => {
            setIsOpen(false);
            setSearchQuery('');
            setResults([]);
          }}
        >
          <div
            style={{
              background: 'var(--bg-primary)',
              width: '90%',
              maxWidth: '700px',
              borderRadius: '8px',
              padding: '32px',
              maxHeight: '80vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '24px', color: 'var(--text-primary)' }}>Search Indotravelista</h2>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setSearchQuery('');
                  setResults([]);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px'
                }}
              >
                <X size={24} color="var(--text-secondary)" />
              </button>
            </div>

            <input
              type="text"
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Search journeys, destinations, experiences..."
              autoFocus
              style={{
                width: '100%',
                padding: '16px',
                fontSize: '16px',
                border: '1px solid var(--border-light)',
                borderRadius: '4px',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                marginBottom: '24px'
              }}
            />

            {searchQuery && results.length === 0 && (
              <p style={{ color: 'var(--text-secondary)', textAlign: 'center' }}>
                No results found for "{searchQuery}"
              </p>
            )}

            {results.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {results.map((result, index) => (
                  <div
                    key={index}
                    onClick={() => handleResultClick(result.url)}
                    style={{
                      padding: '16px',
                      border: '1px solid var(--border-light)',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--gold)';
                      e.currentTarget.style.background = 'var(--bg-secondary)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--border-light)';
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <div style={{ fontSize: '12px', color: 'var(--gold)', marginBottom: '4px' }}>
                      {result.type}
                    </div>
                    <div style={{ fontSize: '16px', fontWeight: '500', color: 'var(--text-primary)', marginBottom: '4px' }}>
                      {result.title}
                    </div>
                    <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                      {result.subtitle}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
